import { Button, Modal } from '@braskai/ui-kit';

import { alpha, css, styled } from '@mui/material';

import { touchHitArea } from '@/shared/lib/touchHitArea';

type CardProps = {
  $isMobile: boolean;
};

export const SDialog = styled(Modal)(
  ({ theme }) => css`
    z-index: 1000;

    .MuiBackdrop-root {
      background-color: ${alpha(theme.palette.grey[900], 0.45)};
      backdrop-filter: blur(3px);
    }

    .MuiDialog-paper {
      border-radius: 18px;
      background-color: transparent;
      box-shadow:
        0 24px 64px -12px ${alpha(theme.palette.grey[900], 0.35)},
        0 0 0 1px ${alpha(theme.palette.grey[900], 0.04)};

      ${theme.breakpoints.up('tablet')} {
        max-width: 480px;
      }
    }
  `,
);

export const SCard = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<CardProps>(
  ({ $isMobile, theme }) => css`
    position: relative;
    width: 100%;
    overflow: hidden;
    border-radius: ${$isMobile ? '18px 18px 0 0' : '18px'};
    background-color: ${theme.palette.common.white};
    color: ${theme.palette.grey[900]};
    animation: recording-share-fade-up 0.22s ease;

    @keyframes recording-share-fade-up {
      from {
        transform: translateY(10px);
        opacity: 0;
      }
      to {
        transform: none;
        opacity: 1;
      }
    }
  `,
);

export const SBody = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<CardProps>(
  ({ $isMobile }) => css`
    padding: ${$isMobile ? '32px 20px 24px' : '36px 36px 28px'};
  `,
);

export const SCloseButton = styled('button')(
  ({ theme }) => css`
    position: absolute;
    top: 18px;
    right: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: ${theme.palette.grey[400]};
    background: transparent;
    font-family: ${theme.typography.fontFamily};
    cursor: pointer;

    &:hover {
      color: ${theme.palette.grey[700]};
      background-color: ${theme.palette.grey[100]};
    }

    ${touchHitArea()};
  `,
);

export const SSuccessBadge = styled('span')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-bottom: 20px;
    border-radius: 50%;
    color: ${theme.palette.success[600]};
    background-color: ${theme.palette.success[50]};
  `,
);

export const STitle = styled('h3')(
  ({ theme }) => css`
    margin: 0 0 10px;
    color: ${theme.palette.grey[900]};
    font-weight: 600;
    font-size: 28px;
    line-height: 26px;
    letter-spacing: -0.01em;
  `,
);

export const SDescription = styled('p')(
  ({ theme }) => css`
    margin: 0 0 24px;
    color: ${theme.palette.grey[500]};
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
  `,
);

export const SLinkField = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 48px;
    padding: 0 6px 0 16px;
    border: 1px solid ${theme.palette.grey[300]};
    border-radius: 10px;
    background-color: ${theme.palette.grey[50]};
  `,
);

export const SLinkInput = styled('input')(
  ({ theme }) => css`
    flex: 1;
    min-width: 0;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    color: ${theme.palette.grey[700]};
    font-weight: 400;
    font-size: 14px;
    font-family: ${theme.typography.fontFamily};
    line-height: 18px;
    text-overflow: ellipsis;
  `,
);

export const SCopyButton = styled(Button)(
  ({ theme }) => css`
    flex-shrink: 0;
    height: 32px;
    min-height: 32px;
    padding: 0 14px;
    color: ${theme.palette.grey[800]};
    box-shadow: none;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    white-space: nowrap;

    & > span {
      display: inline-flex;
      align-items: center;
      gap: 7px;
    }

    &:hover,
    &:focus {
      color: ${theme.palette.grey[800]};
    }

    ${touchHitArea()};
  `,
);

export const SUpsellDivider = styled('div')(
  ({ theme }) => css`
    height: 1px;
    background: ${theme.palette.grey[200]};
  `,
);

export const SUpsellRow = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isMobile',
})<CardProps>(
  ({ $isMobile, theme }) => css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: ${$isMobile ? '16px 20px 24px' : '20px 36px 32px'};
    color: ${theme.palette.primary[500]};
  `,
);

export const SUpsellText = styled('span')(
  ({ theme }) => css`
    flex: 1;
    color: ${theme.palette.grey[600]};
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  `,
);

export const SUpsellButton = styled('button')(
  ({ theme }) => css`
    position: relative;
    flex-shrink: 0;
    padding: 4px 2px;
    border: 0;
    background: transparent;
    color: ${theme.palette.primary[600]};
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    cursor: pointer;

    &:hover {
      color: ${theme.palette.primary[700]};
    }

    ${touchHitArea()};
  `,
);
